import { supabase } from "./supabase";

/**
 * Fetch ALL generators (including those without runs)
 */
export async function fetchAllGenerators() {
  const { data, error } = await supabase
    .from("bm_generators")
    .select("id, name, endpoint_url, auth_type, concurrency_limit, created_at")
    .order("name", { ascending: true });

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Fetch lightweight dashboard data (run-level fields only)
 * Paginates to fetch ALL completed runs (Supabase default limit is 1000)
 * Returns runs with isWithin7Days flag for per-cell fallback behavior
 * Excludes archived runs from dashboard calculations
 */
export async function fetchDashboardData() {
  const SEVEN_DAYS_AGO = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const selectFields =
    "id, generator_id, aggregate_score, pass_rate, avg_generation_latency_s, total_items, items_generated, items_evaluated, error_message, created_at, status, filters, inceptbench_version, curriculum_version, dataset_version, is_static, variety_score, variety_status, bm_generators(id, name)";

  // Fetch all completed runs in batches to avoid Supabase's 1000-row limit
  let allRuns = [];
  let from = 0;
  const pageSize = 1000;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from("bm_runs")
      .select(selectFields)
      .eq("status", "completed")
      .eq("is_archived", false) // Exclude archived runs from dashboard
      .not("aggregate_score", "is", null)
      .order("created_at", { ascending: false })
      .range(from, from + pageSize - 1);

    if (error) {
      throw error;
    }

    if (data && data.length > 0) {
      allRuns = [...allRuns, ...data];
      from += pageSize;
      if (data.length < pageSize) {
        hasMore = false;
      }
    } else {
      hasMore = false;
    }
  }

  // Exclude runs with errors:
  // 1. error_message is set (explicit error report from worker)
  // 2. items_generated != items_evaluated (generation/evaluation errors)
  // 3. items_generated != total_items (some items were never generated)
  // 4. variety_status is set but variety_score is null (variety check failed)
  const errorFreeRuns = allRuns.filter(
    (run) =>
      !run.error_message &&
      run.items_generated === run.items_evaluated &&
      run.items_generated === run.total_items &&
      !(run.variety_status && run.variety_score === null)
  );

  // Mark each run with whether it's within 7 days
  const markedRuns = errorFreeRuns.map((run) => ({
    ...run,
    isWithin7Days: new Date(run.created_at) >= SEVEN_DAYS_AGO,
  }));

  // Check if ALL runs are outside 7 days (global fallback indicator)
  const hasAnyRecentRuns = markedRuns.some((run) => run.isWithin7Days);

  const excludedErrorCount = allRuns.length - errorFreeRuns.length;

  return {
    runs: markedRuns,
    usingFallback: !hasAnyRecentRuns && markedRuns.length > 0,
    excludedErrorCount,
  };
}

/**
 * Fetch raw_requests for specific runIds (on-demand for ambiguous benchmark set resolution)
 */
export async function fetchRawRequestsForRuns(runIds) {
  const { data, error } = await supabase
    .from("bm_runs")
    .select("id, raw_requests")
    .in("id", runIds);

  if (error) {
    throw error;
  }
  return data;
}

/**
 * Fetch raw_evaluation_results and raw_requests for specific runIds (on-demand for drilldown)
 * We need raw_requests to filter by subject/grade
 */
export async function fetchRubricDimensions(runIds) {
  const { data, error } = await supabase
    .from("bm_runs")
    .select("id, raw_requests, raw_evaluation_results")
    .in("id", runIds);

  if (error) {
    throw error;
  }
  return data;
}

// ============ Benchmark Set Derivation Logic ============

/**
 * Determines if a run's benchmark set can be derived from filters alone (unambiguous)
 * Rule: Exactly 1 subject AND exactly 1 grade = unambiguous
 */
function isUnambiguousRun(run) {
  const subjects = run.filters?.subjects || [];
  const grades = run.filters?.grades || [];
  return subjects.length === 1 && grades.length === 1;
}

/**
 * For unambiguous runs: derive benchmark set directly from filters
 */
function deriveBenchmarkSetFromFilters(run) {
  const subject = run.filters.subjects[0].toLowerCase();
  const grade = String(run.filters.grades[0]);
  return [{ subject, grade }];
}

/**
 * For ambiguous runs: derive benchmark sets from raw_requests
 * raw_requests is an object keyed by request_id, each with { subject, grade, ... }
 */
function deriveBenchmarkSetsFromRawRequests(rawRequests) {
  const sets = new Set();
  if (!rawRequests) {
    return [];
  }

  Object.values(rawRequests).forEach((req) => {
    if (req.subject && req.grade) {
      sets.add(`${req.subject.toLowerCase()}_${req.grade}`);
    }
  });

  return Array.from(sets).map((key) => {
    const [subject, grade] = key.split("_");
    return { subject, grade };
  });
}

/**
 * Main processing flow after fetching runs:
 * 1. Categorize runs as unambiguous or ambiguous
 * 2. Fetch raw_requests only for ambiguous runs
 * 3. Derive benchmark sets for all runs
 */
export async function processRunsWithBenchmarkSets(runs) {
  const unambiguousRuns = [];
  const ambiguousRunIds = [];
  const ambiguousRunsMap = new Map();

  runs.forEach((run) => {
    if (isUnambiguousRun(run)) {
      unambiguousRuns.push({
        ...run,
        benchmarkSets: deriveBenchmarkSetFromFilters(run),
      });
    } else {
      ambiguousRunIds.push(run.id);
      ambiguousRunsMap.set(run.id, run);
    }
  });

  // Fetch raw_requests only for ambiguous runs
  let resolvedAmbiguousRuns = [];
  if (ambiguousRunIds.length > 0) {
    const rawRequestsData = await fetchRawRequestsForRuns(ambiguousRunIds);
    const rawRequestsMap = new Map(
      rawRequestsData.map((r) => [r.id, r.raw_requests])
    );

    resolvedAmbiguousRuns = ambiguousRunIds.map((id) => ({
      ...ambiguousRunsMap.get(id),
      benchmarkSets: deriveBenchmarkSetsFromRawRequests(rawRequestsMap.get(id)),
    }));
  }

  return [...unambiguousRuns, ...resolvedAmbiguousRuns];
}

// ============ Matrix Building Logic ============

// Helper to get grade sort order (K = 0, then numeric)
function getGradeOrder(grade) {
  if (grade === "K" || grade === "k") {
    return 0;
  }
  const num = Number(grade);
  return isNaN(num) ? 999 : num;
}

/**
 * Extract generators and benchmark sets from processed runs
 * Shared logic between recent and best-run matrix builders
 */
function extractGeneratorsAndBenchmarks(processedRuns, allGenerators) {
  const generatorsMap = new Map();
  const benchmarkSetsSet = new Set();

  // If allGenerators provided, use it (includes generators without runs)
  if (allGenerators && allGenerators.length > 0) {
    allGenerators.forEach((gen) => {
      generatorsMap.set(gen.id, { ...gen });
    });
  }

  // Also collect generators from runs (in case allGenerators wasn't provided)
  processedRuns.forEach((run) => {
    if (run.bm_generators && !generatorsMap.has(run.generator_id)) {
      generatorsMap.set(run.generator_id, {
        id: run.generator_id,
        name: run.bm_generators.name,
      });
    }

    run.benchmarkSets?.forEach((bs) => {
      benchmarkSetsSet.add(`${bs.subject}_${bs.grade}`);
    });
  });

  const generators = Array.from(generatorsMap.values());
  const benchmarkSets = Array.from(benchmarkSetsSet).map((key) => {
    const [subject, grade] = key.split("_");
    return { subject, grade };
  });

  // Sort benchmark sets by subject then grade
  benchmarkSets.sort((a, b) => {
    if (a.subject !== b.subject) {
      return a.subject.localeCompare(b.subject);
    }
    return getGradeOrder(a.grade) - getGradeOrder(b.grade);
  });

  return { generators, benchmarkSets };
}

/**
 * Build cell runs map from processed runs
 * Groups runs by generator and benchmark key
 */
function buildCellRunsMap(processedRuns) {
  const cellRunsMap = {}; // generatorId -> bmKey -> [runs]

  processedRuns.forEach((run) => {
    const genId = run.generator_id;
    if (!cellRunsMap[genId]) {
      cellRunsMap[genId] = {};
    }

    run.benchmarkSets?.forEach((bs) => {
      const bmKey = `${bs.subject}_${bs.grade}`;
      if (!cellRunsMap[genId][bmKey]) {
        cellRunsMap[genId][bmKey] = [];
      }
      cellRunsMap[genId][bmKey].push(run);
    });
  });

  return cellRunsMap;
}

/**
 * Compute generator coverage stats
 */
function computeGeneratorStats(generators, matrixData, benchmarkSets) {
  const totalBenchmarkSets = benchmarkSets.length;
  const generatorStats = {};

  generators.forEach((gen) => {
    const genData = matrixData[gen.id] || {};
    const coveredCount = Object.keys(genData).length;

    generatorStats[gen.id] = {
      coverageRatio:
        totalBenchmarkSets > 0 ? coveredCount / totalBenchmarkSets : 0,
    };
  });

  return generatorStats;
}

/**
 * Build the matrix data structure from processed runs (recent mode with 7-day fallback)
 * @param processedRuns - Runs with benchmark sets derived
 * @param allGenerators - Optional: All generators from DB (includes those without runs)
 * Returns: { matrixData, generators, benchmarkSets, generatorStats }
 */
export function buildMatrixData(processedRuns, allGenerators = null) {
  const { generators, benchmarkSets } = extractGeneratorsAndBenchmarks(
    processedRuns,
    allGenerators
  );
  const cellRunsMap = buildCellRunsMap(processedRuns);
  const matrixData = {};

  // Build final matrix data with per-cell fallback logic
  Object.entries(cellRunsMap).forEach(([genId, benchmarks]) => {
    matrixData[genId] = {};

    Object.entries(benchmarks).forEach(([bmKey, cellRuns]) => {
      // Sort by created_at descending
      const sortedRuns = cellRuns.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      // Prefer runs within 7 days, but fall back to older runs if none exist
      const recentRuns = sortedRuns.filter((r) => r.isWithin7Days);
      const hasRecentRuns = recentRuns.length > 0;

      // Use the latest recent run if available, otherwise use the latest overall
      const latestRun = hasRecentRuns ? recentRuns[0] : sortedRuns[0];

      const score = latestRun.aggregate_score || 0;
      const passRate = latestRun.pass_rate || 0;

      matrixData[genId][bmKey] = {
        score,
        displayScore: Math.round(score * 100 * 10) / 10,
        passRate,
        displayPassRate: Math.floor(passRate * 100),
        itemsEvaluated: latestRun.items_evaluated || 0,
        runDate: latestRun.created_at,
        runId: latestRun.id,
        isUsingFallback: !hasRecentRuns, // True if this cell is using older data
        inceptbenchVersion: latestRun.inceptbench_version || null,
        curriculumVersion: latestRun.curriculum_version || null,
        datasetVersion: latestRun.dataset_version
          ? String(latestRun.dataset_version)
          : null,
        isStatic: latestRun.is_static === true,
        varietyScore: latestRun.variety_score,
        avgGenerationLatency:
          latestRun.avg_generation_latency_s !== null &&
          latestRun.avg_generation_latency_s !== undefined
            ? parseFloat(latestRun.avg_generation_latency_s)
            : null,
      };
    });
  });

  const generatorStats = computeGeneratorStats(
    generators,
    matrixData,
    benchmarkSets
  );

  return { matrixData, generators, benchmarkSets, generatorStats };
}

/**
 * Build the matrix data structure using BEST (highest pass rate) run per cell
 * @param processedRuns - Runs with benchmark sets derived
 * @param allGenerators - Optional: All generators from DB (includes those without runs)
 * Returns: { matrixData, generators, benchmarkSets, generatorStats }
 */
export function buildMatrixDataBestRun(processedRuns, allGenerators = null) {
  const { generators, benchmarkSets } = extractGeneratorsAndBenchmarks(
    processedRuns,
    allGenerators
  );
  const cellRunsMap = buildCellRunsMap(processedRuns);
  const matrixData = {};

  // Build final matrix data - select BEST run (highest pass rate) per cell
  Object.entries(cellRunsMap).forEach(([genId, benchmarks]) => {
    matrixData[genId] = {};

    Object.entries(benchmarks).forEach(([bmKey, cellRuns]) => {
      // Deprioritize static runs: prefer non-static, then sort by pass_rate
      const sortedRuns = cellRuns.sort((a, b) => {
        const aStatic = a.is_static === true ? 1 : 0;
        const bStatic = b.is_static === true ? 1 : 0;
        if (aStatic !== bStatic) {
          return aStatic - bStatic; // Non-static first
        }
        return (b.pass_rate || 0) - (a.pass_rate || 0);
      });

      const bestRun = sortedRuns[0];
      const score = bestRun.aggregate_score || 0;
      const passRate = bestRun.pass_rate || 0;

      matrixData[genId][bmKey] = {
        score,
        displayScore: Math.round(score * 100 * 10) / 10,
        passRate,
        displayPassRate: Math.floor(passRate * 100),
        itemsEvaluated: bestRun.items_evaluated || 0,
        runDate: bestRun.created_at,
        runId: bestRun.id,
        isUsingFallback: false, // No fallback concept in best run mode
        inceptbenchVersion: bestRun.inceptbench_version || null,
        curriculumVersion: bestRun.curriculum_version || null,
        datasetVersion: bestRun.dataset_version
          ? String(bestRun.dataset_version)
          : null,
        isStatic: bestRun.is_static === true,
        varietyScore: bestRun.variety_score,
        avgGenerationLatency:
          bestRun.avg_generation_latency_s !== null &&
          bestRun.avg_generation_latency_s !== undefined
            ? parseFloat(bestRun.avg_generation_latency_s)
            : null,
      };
    });
  });

  const generatorStats = computeGeneratorStats(
    generators,
    matrixData,
    benchmarkSets
  );

  return { matrixData, generators, benchmarkSets, generatorStats };
}

// ============ Rubric Dimensions Processing ============

/**
 * Build a Set of requestIds that match the given subject/grade
 * Handles both object and array formats for raw_requests
 */
function getMatchingRequestIds(requests, targetSubject, targetGrade) {
  if (!requests) {
    return null; // No requests data, can't filter
  }

  const matchingIds = new Set();
  const normalizedSubject = targetSubject?.toLowerCase();
  const normalizedGrade = String(targetGrade);

  // Handle object format: { requestId: { subject, grade, ... } }
  if (!Array.isArray(requests)) {
    Object.entries(requests).forEach(([requestId, req]) => {
      const reqSubject = req?.subject?.toLowerCase();
      const reqGrade = String(req?.grade || "");

      if (reqSubject === normalizedSubject && reqGrade === normalizedGrade) {
        matchingIds.add(requestId);
      }
    });
  } else {
    // Handle array format: [{ request_id, subject, grade, ... }]
    requests.forEach((req) => {
      const reqSubject = req?.subject?.toLowerCase();
      const reqGrade = String(req?.grade || "");

      if (reqSubject === normalizedSubject && reqGrade === normalizedGrade) {
        // Try both request_id and id fields
        const id = req.request_id || req.id;
        if (id) {
          matchingIds.add(id);
        }
      }
    });
  }

  return matchingIds;
}

/**
 * Extract and aggregate rubric dimension scores from evaluation results
 * Filters by subject and grade to only include relevant items
 */
export function processRubricDimensions(
  evaluationResultsData,
  subject = null,
  grade = null
) {
  const dimensionScores = {};
  let totalItemsProcessed = 0;

  evaluationResultsData.forEach((runData) => {
    const results = runData.raw_evaluation_results;
    const requests = runData.raw_requests;

    if (!results) {
      return;
    }

    // Build set of matching requestIds for this run
    let matchingIds = null;
    if (subject && grade) {
      matchingIds = getMatchingRequestIds(requests, subject, grade);

      // If filter is empty, skip this run entirely
      if (matchingIds && matchingIds.size === 0) {
        return;
      }
    }

    Object.entries(results).forEach(([requestId, item]) => {
      // Filter by matching IDs if we have a filter
      if (matchingIds && !matchingIds.has(requestId)) {
        return;
      }

      const evaluation = item.evaluation;
      if (!evaluation) {
        return;
      }

      totalItemsProcessed++;

      Object.entries(evaluation).forEach(([key, value]) => {
        if (
          key !== "overall" &&
          key !== "overall_rating" &&
          key !== "content_type" &&
          key !== "subcontent_evaluations" &&
          typeof value === "object" &&
          value !== null &&
          typeof value.score === "number"
        ) {
          if (!dimensionScores[key]) {
            dimensionScores[key] = { total: 0, count: 0 };
          }
          dimensionScores[key].total += value.score;
          dimensionScores[key].count += 1;
        }
      });
    });
  });

  // Filter out dimensions that appear in less than 10% of evaluations
  // This removes noise from rare content types with different rubrics
  const minCountThreshold = Math.max(5, Math.floor(totalItemsProcessed * 0.1));

  // Calculate averages and sort ascending (worst first)
  const dimensions = Object.entries(dimensionScores)
    .filter(([, data]) => data.count >= minCountThreshold) // Filter rare dimensions
    .map(([name, data]) => ({
      name,
      averageScore: data.count > 0 ? data.total / data.count : 0,
      displayScore:
        data.count > 0
          ? Math.round((data.total / data.count) * 100 * 10) / 10
          : 0,
      count: data.count,
    }))
    .sort((a, b) => a.averageScore - b.averageScore);

  return dimensions.slice(0, 5); // Top 5 worst
}
